import { getAuthTokenFromLocalStorage } from "hooks/useAuthToken";
import type {
  AgentApprovalMode,
  IAgentApprovalView,
  IAgentCancelJobAnswer,
  IAgentCanvasJobsResponse,
  IAgentChatRequest,
  IAgentSpendingView,
  IAgentThreadSpend,
  IAgentBindThreadRequest,
  IAgentRenameThreadRequest,
  IAgentRequestError,
  IAgentThread,
  IAgentThreadsResponse,
} from "api/agentChat/agentChat.type";
import type {
  CardAnswer,
  CardAnswerResult,
  CardDecision,
  ClientToolResult,
  ToolCallClaim,
  HistoryPage,
  PlanDecision,
  PlanPatch,
  StepOptionChange,
  ResourceRefs,
  Suggestions,
  Turn,
  TurnApproval,
  TurnCanvas,
  TurnEdit,
} from "api/agentChat/agentWire";
import i18n from "i18n";

/**
 * The design agent's routes (HEDEF §3–§4): the thread list and its binding, the history page, the two streams
 * (`threadEvents`, `turnStream` — opened here, read by `agentSse`), and every command. A command answers with its
 * acceptance and the affected object; what it causes arrives on the thread stream (K-69).
 *
 * ⚠️ Not `RefabricProURL`, deliberately: the agent is another origin (`REACT_APP_AGENT_API_URL`,
 * locally `http://localhost:4211`). It carries the SAME bearer token the app's own API calls carry,
 * read at call time. Like every service, nothing here catches: a non-2xx rejects with
 * `AgentRequestError` and the caller decides what it means.
 */
export const agentBaseUrl = (): string => (process.env.REACT_APP_AGENT_API_URL || "").replace(/\/+$/, "");

/**
 * The headers every agent call carries. Fresh per call: the token may have been refreshed. The UI
 * language goes as `Accept-Language`: the agent writes its UI lines (steps, cards) in it, while the
 * model answers in whatever language the user writes (no language detection on the agent).
 */
export const agentHeaders = (): Record<string, string> => {
  const token = getAuthTokenFromLocalStorage();
  const language = i18n.resolvedLanguage || i18n.language;

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(language ? { "Accept-Language": language } : {}),
    "x-request-id": crypto.randomUUID(),
  };
};

/** A non-2xx agent answer, carrying the agent's public error body (never its raw text). */
export class AgentRequestError extends Error implements IAgentRequestError {
  status: number;
  code?: string;
  requestId?: string;
  /** Seconds the agent asks to wait (`Retry-After` on a 429), when it said. */
  retryAfter?: number;
  /** The rest of the error body — e.g. the canvas's existing `threadId` on a bind conflict. */
  extra: Record<string, unknown>;

  constructor(details: IAgentRequestError, extra: Record<string, unknown> = {}) {
    super(details.message || `agent request failed (${details.status})`);
    this.name = "AgentRequestError";
    this.status = details.status;
    this.code = details.code;
    this.requestId = details.requestId;
    if (details.retryAfter !== undefined) this.retryAfter = details.retryAfter;
    this.extra = extra;
  }
}

/** `Retry-After` (RFC 9110 §10.2.3): delay-seconds or an HTTP date, as whole seconds from now; anything else → nothing. */
export const retryAfterOf = (header: string | null, now = Date.now()): { retryAfter?: number } => {
  if (!header) return {};
  const seconds = /^\d+$/.test(header.trim()) ? Number(header.trim()) : Math.ceil((Date.parse(header) - now) / 1000);

  return Number.isFinite(seconds) && seconds >= 0 ? { retryAfter: seconds } : {};
};

const send = async <T>(path: string, init: { method: string; body?: unknown; signal?: AbortSignal }): Promise<T> => {
  const response = await fetch(`${agentBaseUrl()}${path}`, {
    method: init.method,
    headers: { ...agentHeaders(), ...(init.body !== undefined ? { "Content-Type": "application/json" } : {}) },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
    signal: init.signal,
  });
  if (!response.ok) {
    let body: { error?: { code?: string; message?: string; requestId?: string } } & Record<string, unknown> = {};
    try {
      body = await response.json();
    } catch {
      // An empty or non-JSON error body still has a status.
    }
    throw new AgentRequestError(
      {
        status: response.status,
        code: body.error?.code,
        message: body.error?.message,
        requestId: body.error?.requestId ?? response.headers.get("x-request-id") ?? undefined,
        ...retryAfterOf(response.headers.get("retry-after")),
      },
      body
    );
  }

  return (response.status === 204 ? undefined : await response.json()) as T;
};

const segment = encodeURIComponent;

/** A turn or item command's acceptance: the affected object as the agent holds it now, at the thread's `seq`. */
export interface AgentTurnAnswer {
  seq?: number;
  turn?: Turn;
}

/** Opens one SSE stream; a non-2xx is handed back as the response (the reader decides whether to retry). */
const openStream = (path: string, signal: AbortSignal): Promise<Response> => fetch(`${agentBaseUrl()}${path}`, { headers: agentHeaders(), signal });

const withSince = (since: string | undefined) => (since !== undefined ? `?${new URLSearchParams({ since })}` : "");

export default {
  /** Is the agent configured for this build at all? */
  isConfigured: (): boolean => !!agentBaseUrl(),
  /** Newest first. `canvasId` narrows it to that canvas's conversations, all of them (a canvas is a project of chats). */
  listThreads: (params: { page?: number; perPage?: number; canvasId?: string } = {}, signal?: AbortSignal) =>
    send<IAgentThreadsResponse>(
      `/agent/threads?${new URLSearchParams({ page: String(params.page ?? 0), perPage: String(params.perPage ?? 30), ...(params.canvasId ? { canvasId: params.canvasId } : {}) })}`,
      { method: "GET", signal }
    ),
  /** One conversation, as the agent stores it — the server's word on which canvas it belongs to. 404: none, or not the user's. */
  getThread: (threadId: string, signal?: AbortSignal) => send<IAgentThread>(`/agent/threads/${segment(threadId)}`, { method: "GET", signal }),
  bindThread: (body: IAgentBindThreadRequest) => send<IAgentThread>("/agent/threads", { method: "POST", body }),
  renameThread: (threadId: string, body: IAgentRenameThreadRequest) => send<IAgentThread | undefined>(`/agent/threads/${segment(threadId)}`, { method: "PATCH", body }),

  // ── Reading ─────────────────────────────────────────────────────────────────────────────────

  /**
   * One history page (HEDEF §3.4): `TurnView[]` oldest first, the view's `seq` (the thread stream opens from it) and the
   * cursor of the next older page. No cursor: the newest page.
   */
  messages: (threadId: string, params: { cursor?: string; limit?: number } = {}, signal?: AbortSignal) => {
    const query = new URLSearchParams({ ...(params.cursor ? { cursor: params.cursor } : {}), ...(params.limit ? { limit: String(params.limit) } : {}) }).toString();

    return send<HistoryPage>(`/agent/threads/${segment(threadId)}/messages${query ? `?${query}` : ""}`, { method: "GET", signal });
  },
  /** THE thread stream (HEDEF §3.2): `turn`, `items`, `thread`, `cursor`, `reset` events; `id:` = `seq`. */
  threadEvents: (threadId: string, since: string | undefined, signal: AbortSignal) => openStream(`/agent/threads/${segment(threadId)}/events${withSince(since)}`, signal),
  /** One turn's stream (HEDEF §3.3): the AI SDK UI message stream of that turn alone, from the record; 404 when there is no such turn. */
  turnStream: (turnId: string, since: string | undefined, signal: AbortSignal) => openStream(`/agent/turns/${segment(turnId)}/stream${withSince(since)}`, signal),

  // ── Commands (HEDEF §4): by id, idempotent; a stale revision answers 409 with the current object. ──────

  /** A user turn: `turn_id` is made here, so the same send twice is the same turn. `202 {seq, turn}`. */
  chat: (body: IAgentChatRequest) => send<AgentTurnAnswer>("/agent/chat", { method: "POST", body }),
  /** Stops a turn and its running jobs (K-74(1)); a queued one leaves the queue. The agent resolves the chain. */
  cancelTurn: (turnId: string) => send<AgentTurnAnswer>(`/agent/turns/${segment(turnId)}/cancel`, { method: "POST" }),
  /**
   * The waiting canvas tool's CAS base (15-ortak-tuval CONTRACT v2 R-1): the server keeps it on the turn's chain and
   * resolves the chain itself; this side never holds a base.
   */
  turnCanvas: (turnId: string) => send<TurnCanvas>(`/agent/turns/${segment(turnId)}/canvas`, { method: "GET" }),
  /** A failed turn goes back to the queue (same turn id, a new attempt). */
  retryTurn: (turnId: string) => send<AgentTurnAnswer>(`/agent/turns/${segment(turnId)}/retry`, { method: "POST" }),
  /** A queued turn's text (409 once it runs). */
  editTurn: (turnId: string, body: TurnEdit) => send<AgentTurnAnswer>(`/agent/turns/${segment(turnId)}`, { method: "PATCH", body }),
  /** Removes a queued turn (a plan step's turn is not removable: its plan is). */
  deleteTurn: (turnId: string) => send<AgentTurnAnswer>(`/agent/turns/${segment(turnId)}`, { method: "DELETE" }),
  /** The queued turn's gate (a plan step's checkpoint, K-75). */
  approveTurn: (turnId: string, body: TurnApproval) => send<AgentTurnAnswer>(`/agent/turns/${segment(turnId)}/approval`, { method: "POST", body }),
  /** The user turns' order; plan steps keep their relative order. */
  reorderQueue: (threadId: string, turnIds: string[]) => send<{ seq?: number }>(`/agent/threads/${segment(threadId)}/queue/reorder`, { method: "POST", body: { turn_ids: turnIds } }),
  /** Halts / resumes the plan chain's turns (a running turn is not touched). */
  haltQueue: (threadId: string, halted: boolean) => send<{ seq?: number; halted?: boolean }>(`/agent/threads/${segment(threadId)}/queue/halt`, { method: "POST", body: { halted } }),
  /** A question card's answer → the answer item + its answer turn; `pending_submission` → the same body again. */
  answerCard: (itemId: string, body: CardAnswer) => send<CardAnswerResult>(`/agent/cards/${segment(itemId)}/answer`, { method: "POST", body }),
  /** An item approval card's decision (`approve_item`): the same route as a question's answer, its own body. */
  decideCard: (itemId: string, body: CardDecision) => send<CardAnswerResult>(`/agent/cards/${segment(itemId)}/answer`, { method: "POST", body }),
  /** A plan's money card (Ask): approve / reject, with the checkpoint switches. `revision` = the plan's content revision (`planRevisionOf`). */
  decidePlan: (planId: string, body: PlanDecision) => send<AgentTurnAnswer>(`/agent/plans/${segment(planId)}/decision`, { method: "POST", body }),
  /** A plan's checkpoint switches while it waits for approval. */
  patchPlan: (planId: string, body: PlanPatch) => send<{ seq?: number }>(`/agent/plans/${segment(planId)}`, { method: "PATCH", body }),
  /** A step's aspect ratio / resolution on the plan card while it waits for approval (this step only). */
  setStepOption: (planId: string, stepId: string, body: StepOptionChange) => send<{ seq?: number }>(`/agent/plans/${segment(planId)}/steps/${segment(stepId)}/options`, { method: "POST", body }),
  /** A client tool's result → a continuation turn (K-74(4)); a second send changes nothing. */
  toolCallResult: (toolCallId: string, body: ClientToolResult) => send<AgentTurnAnswer>(`/agent/tool-calls/${segment(toolCallId)}/result`, { method: "POST", body }),
  /**
   * W3: claim a `canvas_action` call for THIS tab (lease 60 s; the same tab re-claiming renews). 409 = not this tab, not
   * now (`claimed` + `retry_after_ms`, `awaiting_approval`, `turn_stopped`, `done`) — read from `AgentRequestError.extra`.
   */
  claimToolCall: (toolCallId: string, body: ToolCallClaim) => send<{ leaseUntil: number }>(`/agent/tool-calls/${segment(toolCallId)}/claim`, { method: "POST", body }),
  /** W3: give a held claim back as the page goes away (keepalive: outlives the page; a beacon cannot carry the auth header). */
  releaseToolCallOnLeave: (toolCallId: string, tabId: string): void => {
    try {
      void fetch(`${agentBaseUrl()}/agent/tool-calls/${segment(toolCallId)}/release`, {
        method: "POST",
        keepalive: true,
        headers: { ...agentHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ tabId }),
      }).catch(() => undefined);
    } catch {
      // best effort: the lease expires anyway
    }
  },
  /** Cancels one job (fal `runs.cancel`): accepted, not done — its step item on the thread stream says when it is cancelled. */
  cancelJob: (threadId: string, jobId: string) => send<IAgentCancelJobAnswer>(`/agent/jobs/${segment(jobId)}/cancel`, { method: "POST", body: { threadId } }),
  /**
   * Sends a failed job again (fal `runs.retry`): the same body, a new idempotency key, the same money rules — its new job
   * row arrives on the thread stream, in the job's home; a card it needs is answered on its plan. No body.
   */
  retryJob: (jobId: string) => send<{ seq?: number }>(`/agent/jobs/${segment(jobId)}/retry`, { method: "POST" }),
  /**
   * The canvas's jobs, every conversation's (todo1023/edit-version): all running jobs, and with `since` (epoch ms, the
   * previous answer's `now`) the ones that ended since, in their last state, plus the cards of those that landed.
   */
  canvasJobs: (canvasId: string, since: number | undefined, signal?: AbortSignal) =>
    send<IAgentCanvasJobsResponse>(`/agent/canvases/${segment(canvasId)}/jobs${since !== undefined ? `?${new URLSearchParams({ since: String(since) })}` : ""}`, { method: "GET", signal }),
  /** Three next-message suggestions (not stored). */
  suggestions: (threadId: string, signal?: AbortSignal) => send<Suggestions>(`/agent/threads/${segment(threadId)}/suggestions`, { method: "POST", signal }),
  /** The conversation's spend (S13): jobs, charged and held credits, by task — the agent reads the API's charges live. */
  /** K-82: name, cover and state of the thread's linked resources — ONE call per message (`resourceLinks.ts`). */
  resourceRefs: (threadId: string, handles: string[], signal?: AbortSignal) =>
    send<ResourceRefs>(`/agent/refs/resolve?${new URLSearchParams({ thread: threadId, handles: handles.join(",") })}`, { method: "GET", signal }),
  spend: (threadId: string, signal?: AbortSignal) => send<IAgentThreadSpend>(`/agent/threads/${segment(threadId)}/spend`, { method: "GET", signal }),
  /** Compacts the conversation's context now (the route's name is provisional: coordinator note). */
  compactThread: (threadId: string) => send<{ seq?: number } | undefined>(`/agent/threads/${segment(threadId)}/compact`, { method: "POST" }),

  // ── Settings ─────────────────────────────────────────────────────────────────────────────────

  /** The conversation's approval mode and the account's safety cap — the agent's state, read for the composer's Approvals button. */
  approval: (threadId: string, signal?: AbortSignal) => send<IAgentApprovalView>(`/agent/threads/${segment(threadId)}/approval`, { method: "GET", signal }),
  /**
   * The user's mode choice: a command. The agent validates it (Auto-approve needs a safety cap: 400 `fields: ["cap"]`)
   * and answers with its state, which the button then shows.
   */
  setApprovalMode: (threadId: string, mode: AgentApprovalMode) => send<IAgentApprovalView>(`/agent/threads/${segment(threadId)}/approval`, { method: "PUT", body: { mode } }),
  /** The account's safety cap (fal Settings → Spending caps). */
  spending: (signal?: AbortSignal) => send<IAgentSpendingView>("/agent/settings/spending", { method: "GET", signal }),
  /** Sets the safety cap (credits; null removes it): a command, answered with the agent's state. */
  setSpending: (body: { cap: number | null; uncounted?: string[] }) => send<IAgentSpendingView>("/agent/settings/spending", { method: "PUT", body }),
};
